import { encodeID } from './Helper';
import { Routes } from '../constants/RouteConstants';
import { LanguageConstants } from '../constants/LanguageConstants';

export type PendingActionVariant = 'draft' | 'wire' | 'cc' | 'profile';

export type PendingActionItem = {
  id: string;
  variant: PendingActionVariant;
  stripLabel: string;
  title: string;
  description: string;
  ctaLabel: string;
  to: string;
};

/** Localized copy for draft / saved quotation card (visibility + link only are dynamic). */
export function draftPendingCopy() {
  return {
    stripLabel: (LanguageConstants as any)?.PENDING_ACTION_DRAFT_STRIP ?? 'You Have A Saved Draft',
    title: (LanguageConstants as any)?.PENDING_ACTION_DRAFT_TITLE ?? 'You Have A Saved Draft',
    description:
      (LanguageConstants as any)?.PENDING_ACTION_DRAFT_DESC ??
      'Finish and submit your request to get a quote',
    ctaLabel: (LanguageConstants as any)?.PENDING_ACTION_DRAFT_CTA ?? 'Continue Draft',
  } as const;
}

function isTruthyString(v: unknown): boolean {
  return String(v ?? '').trim().length > 0;
}

function trimStr(v: unknown): string {
  if (v == null) return '';
  return String(v).trim();
}

/**
 * Flatten profile root with optional `business_details` / `businessDetails` block from API.
 */
function mergedBusinessProfile(profile: any): Record<string, any> {
  if (!profile || typeof profile !== 'object') return {};
  const inner = profile.business_details || profile.businessDetails || {};
  return { ...profile, ...inner };
}

function isCancelledOrder(row: any): boolean {
  return String(row?.is_cancel) === '1' || row?.is_cancel === 1;
}

function hasDraftQuoteRows(row: any): boolean {
  if (!row) return false;
  const qd = row?.quotes_detail;
  if (Array.isArray(qd)) return qd.length > 0;
  const itemsCount = Number(row?.items_count ?? row?.quotes_count ?? 0);
  if (Number.isFinite(itemsCount) && itemsCount > 0) return true;

  // /quotes/get rows usually do not include `quotes_detail`; treat explicit draft rows as valid.
  const isDraftStatus = String(row?.status ?? '') === '0';
  const isNewRequest = String(row?.new_request ?? '') === '1';
  const hasQuotationId = String(row?.quotation_id ?? '').trim().length > 0;
  return isDraftStatus && !isNewRequest && hasQuotationId;
}

/** First wire-transfer order still awaiting payment (offline + pending). */
export function findFirstWirePendingOrder(rows: any[] | undefined): any | undefined {
  if (!Array.isArray(rows)) return undefined;
  return rows.find(
    (o) =>
      !isCancelledOrder(o) &&
      String(o?.payment_type) === '1' &&
      String(o?.payment_status) === '0',
  );
}

/** First online (card) order with declined / failed payment (payment_status 0). */
export function findFirstCcDeclinedOrder(rows: any[] | undefined): any | undefined {
  if (!Array.isArray(rows)) return undefined;
  return rows.find(
    (o) =>
      !isCancelledOrder(o) &&
      String(o?.payment_type) === '0' &&
      String(o?.payment_status) === '0',
  );
}

/**
 * Business accounts only: show “Complete profile” if any of these is null/empty —
 * company name, business field type, contact name, company tax id, company email, company address.
 * Reads camelCase/snake_case and merged `business_details` when present on `/auth/profile`.
 */
export function isBusinessProfileIncomplete(profile: any): boolean {
  if (!profile) return false;
  const accountType = String(profile.account_type || '').toLowerCase().trim();
  if (accountType !== 'business') return false;

  const m = mergedBusinessProfile(profile);

  const companyName = trimStr(m.company_name) || trimStr(m.name);
  if (!companyName) return true;

  const businessFieldType =
    trimStr(m.business_field_type) ||
    trimStr(m.businessFieldType) ||
    trimStr(m.field_type) ||
    trimStr(m.field_type_id);
  if (!businessFieldType) return true;

  const contactName = trimStr(m.contact_name) || trimStr(m.contactName);
  if (!contactName) return true;

  const companyTaxId =
    trimStr(m.company_tax_id) ||
    trimStr(m.companyTaxId) ||
    trimStr(m.tax_id) ||
    trimStr(m.vat_number);
  if (!companyTaxId) return true;

  const companyEmail = trimStr(m.company_email) || trimStr(m.companyEmail) || trimStr(m.email);
  if (!companyEmail) return true;

  const companyAddress =
    trimStr(m.company_address) ||
    trimStr(m.companyAddress) ||
    trimStr(m.business_address) ||
    trimStr(m.address_line1) ||
    trimStr(m.address_line_1);
  if (!companyAddress) return true;

  return false;
}

function orderDetailPath(order: any): string {
  const enc = encodeID(order?.uuid);
  return `/order-detail/${enc}`;
}

function orderDisplayId(order: any): string {
  if (isTruthyString(order?.uuid)) return String(order.uuid);
  if (order?.id != null) return String(order.id);
  return '';
}

/**
 * Builds pending-action cards in display order: draft → wire → cc → profile.
 */
export function buildPendingDashboardItems(params: {
  profile: any | null;
  orderRows: any[] | null;
  draftQuoteRows: any[] | null;
}): PendingActionItem[] {
  const items: PendingActionItem[] = [];
  const { profile, orderRows, draftQuoteRows } = params;

  const drafts = (Array.isArray(draftQuoteRows) ? draftQuoteRows : []).filter(hasDraftQuoteRows);
  if (drafts.length > 0) {
    const first = drafts[0];
    const qid = first?.quotation_id;
    const copy = draftPendingCopy();
    items.push({
      id: `draft-${qid ?? first?.draft_id ?? first?.id ?? 'any'}`,
      variant: 'draft',
      stripLabel: copy.stripLabel,
      title: copy.title,
      description: copy.description,
      ctaLabel: copy.ctaLabel,
      /** Open Drafts page (device + account drafts); continue from there. */
      to: Routes.Drafts,
    });
  }

  const wireOrder = findFirstWirePendingOrder(orderRows ?? undefined);
  if (wireOrder) {
    const oid = orderDisplayId(wireOrder);
    items.push({
      id: `wire-${oid || wireOrder.id}`,
      variant: 'wire',
      stripLabel:
        (LanguageConstants as any)?.PENDING_ACTION_WIRE_STRIP ??
        'Complete your payment to keep things moving',
      title:
        (LanguageConstants as any)?.PENDING_ACTION_WIRE_TITLE ??
        'Complete Your Payment To Keep Things Moving',
      description: oid
        ? String((LanguageConstants as any)?.PENDING_ACTION_WIRE_DESC_WITH_ID ?? 'Your Order {id} is pending a wire transfer').replace('{id}', oid)
        : (LanguageConstants as any)?.PENDING_ACTION_WIRE_DESC ??
          'You have an order pending a wire transfer',
      ctaLabel: (LanguageConstants as any)?.PENDING_ACTION_WIRE_CTA ?? 'View Payment Details',
      to: orderDetailPath(wireOrder),
    });
  }

  const ccOrder = findFirstCcDeclinedOrder(orderRows ?? undefined);
  if (ccOrder) {
    const oid = orderDisplayId(ccOrder);
    items.push({
      id: `cc-${oid || ccOrder.id}`,
      variant: 'cc',
      stripLabel: (LanguageConstants as any)?.PENDING_ACTION_CC_STRIP ?? "Payment didn't go through",
      title: (LanguageConstants as any)?.PENDING_ACTION_CC_TITLE ?? "Payment didn't go through",
      description: oid
        ? String((LanguageConstants as any)?.PENDING_ACTION_CC_DESC_WITH_ID ?? 'Order {id}: payment did not go through. Please check your details or try again to continue.').replace('{id}', oid)
        : (LanguageConstants as any)?.PENDING_ACTION_CC_DESC ??
          'No worries, it happens! Please check your details or try again to continue.',
      ctaLabel: (LanguageConstants as any)?.PENDING_ACTION_CC_CTA ?? 'Update Payment Details',
      to: orderDetailPath(ccOrder),
    });
  }

  if (isBusinessProfileIncomplete(profile)) {
    items.push({
      id: 'profile-business-incomplete',
      variant: 'profile',
      stripLabel: (LanguageConstants as any)?.PENDING_ACTION_PROFILE_STRIP ?? 'Complete your profile',
      title: (LanguageConstants as any)?.PENDING_ACTION_PROFILE_TITLE ?? 'Complete profile',
      description:
        (LanguageConstants as any)?.PENDING_ACTION_PROFILE_DESC ??
        'Just one more step, complete your profile to start quoting.',
      ctaLabel: (LanguageConstants as any)?.PENDING_ACTION_PROFILE_CTA ?? 'Complete Profile',
      to: '/profile',
    });
  }

  return items;
}
